import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwOrmDataChangeEvent, DwConfigService, DwConfigServiceToken, DwEventService, DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { asapScheduler, combineLatest, defer, EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, expand, map, observeOn, reduce, shareReplay, switchMap, take, timeout, throwIfEmpty } from 'rxjs/operators';
import { AppMetaData, ChallengeDefinitionEntity, ChallengeTeamEntity, ChallengeTeamMemberEntity, ChallengeTeamJoinRequestEntity, DwUserEntity, UserProfileEntity, UserChallengeEntity, RoUserProfileDisplayEntity, RoTeamDisplayEntity } from '../../../meta-data/app-meta-data.service';
import type { DirectoryUser } from '../../shared/services/profile-routing/profile-directory.service';
import { FitnessMediaService } from '../../shared/services/fitness-media.service';
import { FitnessReferenceDataService } from '../../shared/services/fitness-reference-data.service';

export interface PublicStreakerIdentity {
    UserChallengeId: number; UserProfileId: number; UserId: number; FirstName: string; LastName: string;
    UserPictureMediaId?: number; UserBackgroundMediaId?: number; ProfileSlug?: string; Appearance?: UserProfileEntity;
}
export interface DisplayTeam extends ChallengeTeamEntity { resolvedDisplay?: boolean; }
export interface CentralTeamChange { teamId: number; kind?: 'team' | 'membership'; team?: DisplayTeam; member?: ChallengeTeamMemberEntity; removed?: boolean; }

/** Session memory only. Business reads stay typed; account/API scope and writes invalidate snapshots. */
@Injectable({ providedIn: 'root' })
export class CentralDataService {
    private readonly _profileDisplayPageSize = 200;
    private entries = new Map<string, { expires: number; stream: Observable<unknown>; value?: unknown }>();
    private revision = 0;
    private teamRevision = 0;
    private pendingTeamDisplays = new Set<number>();
    private pendingTeamReconciles = new Set<number>();
    private teamChanges = new Subject<CentralTeamChange>();
    readonly teamChanged$ = this.teamChanges.asObservable();
    private invitationChanges = new Subject<void>();
    readonly invitationsChanged$ = this.invitationChanges.asObservable().pipe(observeOn(asapScheduler));
    private changed = new Subject<void>();
    // Allow other CAP cache invalidation subscribers to finish before consumers reload.
    readonly changed$ = this.changed.asObservable().pipe(observeOn(asapScheduler));
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private security: DwSecurityUserService, @Inject(DwConfigServiceToken) private config: DwConfigService,
        events: DwEventService, private reference: FitnessReferenceDataService, private http: HttpClient, private media: FitnessMediaService) {
        security.securityContextChanged().subscribe(() => this.invalidate());
        [AppMetaData.DwUser, AppMetaData.UserProfile, AppMetaData.UserChallenge, AppMetaData.ChallengeDefinition,
            AppMetaData.ChallengeTeamJoinRequest,
            AppMetaData.UserFitnessSummary, AppMetaData.RoDonationsByTeam, AppMetaData.RoUserDonations, AppMetaData.YearsStreaking,
            AppMetaData.DwAddress, AppMetaData.DwCountry, AppMetaData.DwStateProvince].forEach(item =>
            events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item.ItemDetail.itemName))
                .subscribe(() => this.invalidate()));
        events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.EmailChallenge.ItemDetail.itemName))
            .subscribe(() => this.invitationChanges.next());
        events.getEvent<DwOrmDataChangeEvent>(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.ChallengeTeam.ItemDetail.itemName))
            .subscribe(envelope => {
                const event = envelope?.data;
                const team: ChallengeTeamEntity = { ...event?.originalEntity, ...event?.newEntity };
                // Coalesce notifications until local save handlers have applied their server result.
                if (team.ChallengeTeamId > 0) {
                    const id = team.ChallengeTeamId;
                    this.pendingTeamReconciles.add(id);
                    const scope = this.scope();
                    asapScheduler.schedule(() => {
                        if (scope !== this.scope() || !this.pendingTeamReconciles.delete(id)) { return; }
                        this.teamRevision++;
                        this.entries.delete(this.scope() + 'team:' + id);
                        // Other-tab notifications are reconciled through a secured CAP read.
                        this.team(id).pipe(take(1)).subscribe(rows => {
                            if (rows.length === 1) { this.updateTeam(rows[0]); }
                            else if (!rows.length) { const removed = AppMetaData.ChallengeTeam.CreateEntity(); removed.ChallengeTeamId = id; this.updateTeam(removed, true); }
                        }, () => {});
                    });
                } else { this.expireTeams(false); this.teamChanges.next({ teamId: 0, kind: 'team' }); }
            });
        events.getEvent<DwOrmDataChangeEvent>(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.ChallengeTeamMember.ItemDetail.itemName))
            .subscribe(envelope => {
                const event = envelope?.data;
                const member: ChallengeTeamMemberEntity = { ...event?.originalEntity, ...event?.newEntity };
                this.expireTeams(true);
                this.teamChanges.next({ teamId: member.ChallengeTeamId || 0, kind: 'membership' });
            });
    }
    /** Saved server results update mounted views; only dependent team snapshots expire. */
    updateTeam(team: DisplayTeam, removed = false): void {
        this.pendingTeamReconciles.delete(team?.ChallengeTeamId);
        const scope = this.scope();
        const snapshots = Array.from(this.entries.entries()).filter(([key]) => key.startsWith(scope)
            && /^(team:|team-display:|public-teams:|member-teams:)/.test(key.slice(scope.length)));
        this.expireTeams(false);
        for (const [key, cached] of snapshots) {
            if (!Array.isArray(cached.value)) { continue; }
            const name = key.slice(scope.length), rows = cached.value as ChallengeTeamEntity[];
            if (!team?.ChallengeTeamId) { continue; }
            if (name.startsWith("team:") && team.resolvedDisplay) { this.entries.set(key, cached); continue; }
            let updated = rows.map(row => row.ChallengeTeamId === team.ChallengeTeamId ? { ...row, ...team } : row)
                .filter(row => !(removed && row.ChallengeTeamId === team.ChallengeTeamId));
            if (name.startsWith('public-teams:')) {
                updated = updated.filter(row => row.IsPrivateTeam !== true);
                if (!removed && team.IsPrivateTeam === false && name === 'public-teams:' + team.ChallengeDefinitionId
                    && !updated.some(row => row.ChallengeTeamId === team.ChallengeTeamId)) { updated = [...updated, team]; }
            }
            this.entries.set(key, { ...cached, value: updated, stream: of(updated) });
        }
        this.teamChanges.next({ teamId: team?.ChallengeTeamId || 0, kind: 'team', team, removed });
        if (!removed && team?.ChallengeTeamId && !team.resolvedDisplay && !this.pendingTeamDisplays.has(team.ChallengeTeamId)) {
            const id = team.ChallengeTeamId, scope = this.scope();
            this.pendingTeamDisplays.add(id); this.expireSnapshot('team-display:' + id);
            this.displayTeam(id).pipe(take(1)).subscribe(rows => {
                if (scope !== this.scope()) { return; }
                this.pendingTeamDisplays.delete(id);
                if (rows.length === 1) { this.updateTeam(rows[0]); }
            }, () => { if (scope === this.scope()) { this.pendingTeamDisplays.delete(id); } });
        }
    }
    updateMember(member: ChallengeTeamMemberEntity, removed = false): void {
        this.expireTeams(true);
        this.teamChanges.next({ teamId: member?.ChallengeTeamId || 0, kind: 'membership', member, removed });
    }
    private expireTeams(membership: boolean): void {
        this.teamRevision++;
        const scope = this.scope();
        for (const key of this.entries.keys()) {
            const name = key.slice(scope.length);
            if ((!membership && (name.startsWith('team:') || name.startsWith('team-display:') || name.startsWith('public-teams:') || name.startsWith('member-teams:') || name.startsWith('donations:')))
                || (membership && (name.startsWith('team:') || name.startsWith('team-display:') || name.startsWith('member-teams:') || name.startsWith('memberships:') || name.startsWith('team-summaries:') || name.startsWith('donations:')))) {
                this.entries.delete(key);
            }
        }
    }
    definitions(): Observable<ChallengeDefinitionEntity[]> {
        return this.snapshot('challenge-definitions', () => this.reference.getChallengeDefinitions());
    }
    invalidate(): void { this.pendingTeamDisplays.clear(); this.pendingTeamReconciles.clear(); this.revision++; this.entries.clear(); this.changed.next(); }
    cacheScope(): string { return this.scope(); }
    expireSnapshot(key: string): void { this.entries.delete(this.scope() + key); }
    cacheValue<T>(key: string): T { const entry = this.entries.get(this.scope() + key); return entry && entry.expires > Date.now() ? entry.value as T : undefined; }
    storeSnapshot<T>(key: string, value: T, ttl = 300000): void { this.entries.set(this.scope() + key, { expires: Date.now() + ttl, value, stream: of(value) }); }
    private scope(): string {
        const context = this.security.securityContext;
        return JSON.stringify([this.config.coreConfig.apiRoot, context?.ApiRoot,
            context?.AuthenticatedUser?.UserId, context?.ApplicationUser?.UserId, this.revision]);
    }
    /** Shares both completed snapshots and concurrent subscriptions. Failed reads remain retryable. */
    snapshot<T>(key: string, read: () => Observable<T>, ttl = 300000): Observable<T> {
        return defer(() => {
            const scope = this.scope(), cacheKey = scope + key;
            const teamRevision = this.teamRevision;
            const teamSnapshot = /^(team:|team-display:|public-teams:|member-teams:|memberships:|team-summaries:|donations:)/.test(key);
            const existing = this.entries.get(cacheKey);
            if (existing && existing.expires > Date.now()) { return existing.stream as Observable<T>; }
            const stream = defer(read).pipe(timeout(30000), take(1), throwIfEmpty(() => new Error('Central did not return a result.')), map(value => {
                if (scope !== this.scope() || (teamSnapshot && teamRevision !== this.teamRevision)) { throw new Error('Your account changed. Please try again.'); }
                if (this.entries.get(cacheKey)?.stream === stream) { this.entries.get(cacheKey).value = value; }
                return value;
            }), catchError(error => {
                if (this.entries.get(cacheKey)?.stream === stream) { this.entries.delete(cacheKey); }
                return throwError(error);
            }), shareReplay({ bufferSize: 1, refCount: true }));
            this.entries.set(cacheKey, { expires: Date.now() + ttl, stream });
            return stream;
        });
    }
    userProfile(userId: number): Observable<UserProfileEntity[]> {
        return this.snapshot('profile:' + userId, () => this.orm.executeQuery<UserProfileEntity>(
            AppMetaData.UserProfile.CreateQueryBuilder()
                .addFields(a => [a.UserProfileId, a.UserId, a.ProfileSlug, a.UserDescription, a.MyChallengeCode])
                .addFilterAnd(f => f.Equal(a => a.UserId, userId))));
    }
    enrollment(profileId: number, challengeId: number): Observable<UserChallengeEntity[]> {
        return this.snapshot(`enrollment:${profileId}:${challengeId}`, () => this.orm.executeQuery<UserChallengeEntity>(
            AppMetaData.UserChallenge.CreateQueryBuilder()
                .addFields(a => [a.UserChallengeId, a.UserProfileId, a.ChallengeDefinitionId, a.IsRegistrationComplete])
                .addFilterAnd(f => f.Equal(a => a.UserProfileId, profileId))
                .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))));
    }
    private ownIdentity(userId: number, loaded?: DwUserEntity): DwUserEntity {
        const current = this.security.securityContext?.ApplicationUser;
        const actor = current?.UserId === userId ? current : null;
        if (!loaded && !actor) { return null; }
        return { ...AppMetaData.DwUser.CreateEntity(), ...loaded, UserId: userId,
            FirstName: loaded?.FirstName || actor?.FirstName, LastName: loaded?.LastName || actor?.LastName,
            UserPictureMediaId: loaded?.UserPictureMediaId ?? actor?.UserPictureMediaId,
            UserBackgroundMediaId: loaded?.UserBackgroundMediaId ?? actor?.UserBackgroundMediaId };
    }
    identity(userId: number): Observable<DwUserEntity> {
        return this.snapshot('identity:' + userId, () => this.orm.executeQuery<DwUserEntity>(
            AppMetaData.DwUser.CreateQueryBuilder()
                .addFields(a => [a.UserId, a.FirstName, a.LastName, a.UserPictureMediaId, a.UserBackgroundMediaId])
                .addFilterAnd(f => f.Equal(a => a.UserId, userId)).addPrefetch(r => r.UserProfile))
            .pipe(map(rows => this.ownIdentity(userId, rows[0])), catchError(error => {
                const own = this.ownIdentity(userId); return own ? of(own) : throwError(error);
            })));
    }
    /** Scope the display join in SQL instead of sending a roster-sized literal ID list. */
    challengeProfileDisplays(challengeId: number, completedOnly = true): Observable<DirectoryUser[]> {
        if (!(challengeId > 0)) { return of([]); }
        return this.snapshot(`challenge-profile-displays:${challengeId}:${completedOnly}`, () => {
            const scope = this.scope();
            const loadPage = (pageNo: number) => defer(() => {
                if (scope !== this.scope()) { throw new Error('Your account changed. Please try again.'); }
                return this.profileDisplayPage(challengeId, completedOnly, pageNo).pipe(map(rows => {
                    if (scope !== this.scope()) { throw new Error('Your account changed. Please try again.'); }
                    return rows;
                }));
            });
            // setDistinct creates CAP paging with a default of 50. Read every
            // ordered page before publishing or caching the complete directory.
            return loadPage(1).pipe(
                expand((rows, index) => rows.length === this._profileDisplayPageSize ? loadPage(index + 2) : EMPTY),
                reduce((all, rows) => all.concat(rows), [] as RoUserProfileDisplayEntity[]),
                map(rows => rows.map(row => this.profileDisplay(row))));
        });
    }
    private profileDisplayPage(challengeId: number, completedOnly: boolean, pageNo: number): Observable<RoUserProfileDisplayEntity[]> {
        const query = AppMetaData.RoUserProfileDisplay.CreateQueryBuilder();
        // CAP's generated-relation overload returns undefined at runtime.
        query.addRelation(r => r.UserChallengeByUserProfileId);
        query.addFilterAnd(f => f.Equal(() => AppMetaData.UserChallenge.Attributes.ChallengeDefinitionId, challengeId))
            .addOrderBy(a => a.UserProfileId)
            .setupPaging(pageNo, this._profileDisplayPageSize, false)
            .setDistinct(true);
        if (completedOnly) { query.addFilterAnd(f => f.Equal(() => AppMetaData.UserChallenge.Attributes.IsRegistrationComplete, true)); }
        return this.orm.executeQuery<RoUserProfileDisplayEntity>(query).pipe(take(1));
    }
    profileBySlug(slug: string): Observable<DirectoryUser[]> {
        const query = AppMetaData.RoUserProfileDisplay.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.ProfileSlug, slug));
        return this.snapshot('profile-slug:' + slug, () => this.orm.executeQuery<RoUserProfileDisplayEntity>(query)).pipe(map(rows => rows.map(row => this.profileDisplay(row))));
    }
    private seedDisplay(row: RoUserProfileDisplayEntity | RoTeamDisplayEntity): void {
        this.media.seed([{ MediaId: row.AvatarMediaId, MediaPath: row.AvatarMediaPath, ImageWidth: row.AvatarWidth, ImageHeight: row.AvatarHeight },
            { MediaId: row.BannerMediaId, MediaPath: row.BannerMediaPath, ImageWidth: row.BannerWidth, ImageHeight: row.BannerHeight }]);
    }
    private profileDisplay(row: RoUserProfileDisplayEntity): DirectoryUser {
        this.seedDisplay(row);
        return { ...AppMetaData.UserProfile.CreateEntity(), ...row, _itemName: AppMetaData.UserProfile.ItemDetail.itemName, resolvedDisplay: true, AppearancePresetId: undefined, AvatarImageMode: 1, BannerImageMode: 1,
            UserPictureMediaId: row.AvatarMediaId, UserBackgroundMediaId: row.BannerMediaId };
    }
    private displayTeams(rows: RoTeamDisplayEntity[], scope: string): DisplayTeam[] {
        if (scope !== this.scope()) { throw new Error("Account changed while teams were loading."); }
        return rows.map(row => this.teamDisplay(row));
    }
    private teamDisplay(row: RoTeamDisplayEntity): DisplayTeam {
        this.seedDisplay(row);
        const display: DisplayTeam = { ...AppMetaData.ChallengeTeam.CreateEntity(), ...row, _itemName: AppMetaData.ChallengeTeam.ItemDetail.itemName,
            resolvedDisplay: true, AppearancePresetId: undefined, AvatarImageMode: 1, BannerImageMode: 1,
            TeamPictureMediaId: row.AvatarMediaId, TeamBackgroundMediaId: row.BannerMediaId };
        this.storeSnapshot('team-display:' + row.ChallengeTeamId, [display]);
        return display;
    }
    publicIdentities(challengeId: number): Observable<PublicStreakerIdentity[]> {
        return this.streakers(challengeId).pipe(map(rows => rows.map(row => ({ UserChallengeId: row.UserChallengeId,
            UserProfileId: row.UserProfileId, UserId: row.UserId, FirstName: row.FirstName, LastName: row.LastName,
            ProfileSlug: row.ProfileSlug, UserPictureMediaId: row.UserPictureMediaId, UserBackgroundMediaId: row.UserBackgroundMediaId,
            Appearance: { ...AppMetaData.UserProfile.CreateEntity(), ...row } }))));
    }
    streakers(challengeId: number): Observable<DirectoryUser[]> {
        return this.snapshot('streakers:' + challengeId, () => {
            const registrations = AppMetaData.UserChallenge.CreateQueryBuilder().addFields(a => [a.UserChallengeId, a.UserProfileId, a.ChallengeDefinitionId])
                .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId)).addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true));
            return this.orm.executeQuery<UserChallengeEntity>(registrations).pipe(take(1), switchMap(rows =>
                this.challengeProfileDisplays(challengeId).pipe(map(profiles => {
                    const byProfile = new Map(profiles.map(profile => [profile.UserProfileId, profile]));
                    return rows.map(row => {
                        const profile = byProfile.get(row.UserProfileId);
                        return profile ? { ...profile, UserChallengeId: row.UserChallengeId, ChallengeDefinitionId: row.ChallengeDefinitionId } : null;
                    }).filter(Boolean);
                }))));
        });
    }
    publicTeams(challengeId: number): Observable<DisplayTeam[]> {
        const query = AppMetaData.RoTeamDisplay.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsPrivateTeam, false));
        const scope = this.scope();
        return this.snapshot('public-teams:' + challengeId, () => this.orm.executeQuery<RoTeamDisplayEntity>(query).pipe(map(rows => this.displayTeams(rows, scope))));
    }
    memberTeams(challengeId: number, teamIds: number[]): Observable<DisplayTeam[]> {
        if (!teamIds.length) { return of([]); }
        const ids = Array.from(new Set(teamIds)).sort((a, b) => a - b);
        const query = AppMetaData.RoTeamDisplay.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.InList(a => a.ChallengeTeamId, ids));
        const scope = this.scope();
        return this.snapshot('member-teams:' + challengeId + ':' + ids.join(','), () => this.orm.executeQuery<RoTeamDisplayEntity>(query).pipe(map(rows => this.displayTeams(rows, scope))));
    }
    displayTeam(teamId: number): Observable<DisplayTeam[]> {
        const query = AppMetaData.RoTeamDisplay.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, teamId));
        const scope = this.scope();
        return this.snapshot('team-display:' + teamId, () => this.orm.executeQuery<RoTeamDisplayEntity>(query).pipe(map(rows => this.displayTeams(rows, scope))));
    }
    /** Editable forms retain the original entity and raw appearance/media values. */
    team(teamId: number): Observable<ChallengeTeamEntity[]> {
        return this.snapshot('team:' + teamId, () => this.orm.executeQuery<ChallengeTeamEntity>(
            AppMetaData.ChallengeTeam.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, teamId))));
    }
    memberships(enrollmentId: number): Observable<ChallengeTeamMemberEntity[]> {
        return this.snapshot('memberships:' + enrollmentId, () => this.orm.executeQuery<ChallengeTeamMemberEntity>(
            AppMetaData.ChallengeTeamMember.CreateQueryBuilder()
                .addFields(a => [a.ChallengeTeamMemberId, a.ChallengeTeamId, a.UserChallengeId, a.IsTeamAdmin])
                .addFilterAnd(f => f.Equal(a => a.UserChallengeId, enrollmentId))));
    }
    joinRequests(enrollmentId: number): Observable<ChallengeTeamJoinRequestEntity[]> {
        return this.snapshot('join-requests:' + enrollmentId, () => this.orm.executeQuery<ChallengeTeamJoinRequestEntity>(
            AppMetaData.ChallengeTeamJoinRequest.CreateQueryBuilder()
                .addFields(a => [a.ChallengeTeamJoinRequestId, a.ChallengeTeamId, a.ChallengeTeamJoinRequestStatusId])
                .addFilterAnd(f => f.Equal(a => a.UserChallengeId, enrollmentId))
                .setFieldSettings(settings => settings.LoadAllLookupDisplayFields = true)));
    }
}
